import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountInfo,
  Identity,
  PrefBag,
  PrefWriteBag,
  Signature,
} from '@zimbra-webif/sdk';
import { useClient } from './ClientContext';

/* -------------------------------------------------------------------------- */
/* General preferences                                                         */
/* -------------------------------------------------------------------------- */

/** The handful of `zimbraPref*` keys the General tab reads/writes. */
export const GENERAL_PREF_KEYS = [
  'zimbraPrefFromDisplay',
  'zimbraPrefFromAddress',
  'zimbraPrefMailItemsPerPage',
  'zimbraPrefLocale',
] as const;

/** GetPrefs over the General keys -> flat `_attrs`-style bag. */
export function usePrefs() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['settings', 'prefs'],
    queryFn: (): Promise<PrefBag> => client.prefs.get([...GENERAL_PREF_KEYS]),
  });
}

/** ModifyPrefs — write only the changed keys (per-pref set semantics). */
export function useSavePrefs() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (changes: PrefWriteBag) => client.prefs.modify(changes),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'prefs'] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Account info (default-from candidates + display)                            */
/* -------------------------------------------------------------------------- */

/** GetInfo — the account name is the default send-from address. */
export function useAccountInfo() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['settings', 'info'],
    queryFn: (): Promise<AccountInfo> => client.account.getInfo(['idents', 'sigs']),
  });
}

/* -------------------------------------------------------------------------- */
/* Signatures                                                                  */
/* -------------------------------------------------------------------------- */

export function useSignatures() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['settings', 'signatures'],
    queryFn: (): Promise<Signature[]> => client.identities.getSignatures(),
  });
}

export function useCreateSignature() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; text: string }) =>
      client.identities.createSignature({ name: input.name, text: input.text }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'signatures'] });
    },
  });
}

export function useModifySignature() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string; text: string }) =>
      client.identities.modifySignature(input.id, { name: input.name, text: input.text }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'signatures'] });
    },
  });
}

export function useDeleteSignature() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.identities.deleteSignature({ id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'signatures'] });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Identities                                                                  */
/* -------------------------------------------------------------------------- */

export function useIdentities() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['settings', 'identities'],
    queryFn: (): Promise<Identity[]> => client.identities.getIdentities(),
  });
}

/* -------------------------------------------------------------------------- */
/* Vacation / out-of-office                                                    */
/* -------------------------------------------------------------------------- */

/** A typed slice of the vacation `zimbraPref*` attrs the UI edits. */
export interface VacationForm {
  enabled: boolean;
  reply: string;
  fromDate: string;
  untilDate: string;
}

export function useVacation() {
  const { client } = useClient();
  return useQuery({
    queryKey: ['settings', 'vacation'],
    queryFn: () => client.prefs.getVacation(),
  });
}

export function useSaveVacation() {
  const { client } = useClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (form: VacationForm) =>
      client.prefs.setVacation({
        enabled: form.enabled,
        reply: form.reply,
        fromDate: form.fromDate,
        untilDate: form.untilDate,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'vacation'] });
    },
  });
}
